import React, { useState, useEffect } from "react";
import { Container, Card, CardBody, Input, Badge, Button, Spinner, Row, Col } from "reactstrap";
import { Search, Briefcase, Calendar, User, ArrowRight, Hash, Filter } from "lucide-react";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import Pagination from "../../Helpers/Pagination";
import AdminHeader from "../Admin/Header";

const STATUS_META = {
  PENDING: { label: "Pending", color: "warning", icon: "bx-time" },
  ACCEPTED: { label: "Accepted", color: "info", icon: "bx-check-double" },
  REJECTED: { label: "Rejected", color: "danger", icon: "bx-x-circle" },
  COMPLETED: { label: "Completed", color: "success", icon: "bx-badge-check" },
};

const AdminDealList = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 10;

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const res = await Handler({
        method: "GET",
        url: `/mini_crm/deals_list.php?page=${page}&limit=${LIMIT}&q=${query}`,
      });
      if (res.success) {
        setList(res.data?.list || []);
        setTotal(res.data?.pagination?.total || 0);
      } else {
        toast.error(res.message || "Failed to load deals");
      }
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeals(); }, [page, query]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  return (
    <div className="bg-soft-blue min-vh-100 pb-5">
      <AdminHeader />
      
      <Container py="4" className="mt-4">
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <div className="d-flex align-items-center mb-2">
                <div className="icon-box-navy me-2">
                    <Briefcase size={20} />
                </div>
                <h3 className="fw-bold text-navy mb-0">Deal Management</h3>
            </div>
            <p className="text-muted small mb-0 ml-48">Monitoring all vendor-to-vendor transactions</p>
          </div>
          <Badge color="white" className="text-navy border shadow-sm px-3 py-2">
            Total Deals: {total}
          </Badge>
        </div>

        {/* Search & Filter Bar */}
        <Card className="border-0 shadow-sm mb-4 rounded-4">
          <CardBody className="p-3">
            <form onSubmit={handleSearch}>
              <Row className="g-2 align-items-center">
                <Col md={6}>
                  <div className="search-input-wrap">
                    <Search size={18} className="search-icon" />
                    <Input
                      className="form-control-clean"
                      placeholder="Search by vendor name, receiver or deal title..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </Col>
                <Col md={2}>
                   <Button color="navy" className="w-100 shadow-sm d-flex align-items-center justify-content-center gap-2" type="submit">
                     Search
                   </Button>
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2">Fetching deals...</p>
          </div>
        ) : list.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-4 py-5 text-center">
            <Briefcase size={48} className="text-muted mx-auto mb-3 opacity-25" />
            <h5 className="text-navy fw-bold">No Deals Found</h5>
            <p className="text-muted small">Try adjusting your search criteria.</p>
          </Card>
        ) : (
          <div className="deal-grid">
            {list.map((deal) => {
              const meta = STATUS_META[deal.status] || STATUS_META.PENDING;
              return (
                <Card key={deal.id} className="deal-card border-0 shadow-sm mb-3 rounded-4 overflow-hidden">
                  <CardBody className="p-0">
                    <div className="d-flex flex-column flex-md-row">
                      {/* Left: Status Accent */}
                      <div className={`status-accent bg-${meta.color}`}></div>
                      
                      <div className="p-3 flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-navy fw-bold small d-flex align-items-center">
                                <User size={14} className="me-1"/> {deal.initiator_name}
                            </span>
                            <ArrowRight size={14} className="text-muted" />
                            <span className="text-navy fw-bold small">{deal.receiver_name}</span>
                          </div>
                          <Badge color={`soft-${meta.color}`} className={`text-${meta.color} border-0 rounded-pill px-3`}>
                            {meta.label}
                          </Badge>
                        </div>

                        <h5 className="deal-title text-navy fw-bold mb-1">{deal.title || "No Title Provided"}</h5>
                        <p className="deal-description text-muted mb-3">{deal.description}</p>

                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                          <div className="d-flex gap-3">
                            <div className="text-muted small d-flex align-items-center">
                              <Calendar size={14} className="me-1" />
                              {new Date(deal.created_at).toLocaleDateString("en-IN", {
                                day: "2-digit", month: "short", year: "numeric",
                              })}
                            </div>
                            <div className="text-muted small d-flex align-items-center">
                              <Hash size={14} className="me-1" />
                              ID: {deal.id}
                            </div>
                          </div>
                          
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-4 d-flex justify-content-center">
            <Pagination
                page={page}
                total={total}
                limit={LIMIT}
                onPrev={() => setPage((p) => p - 1)}
                onNext={() => setPage((p) => p + 1)}
            />
        </div>
      </Container>

      <style>{`
        .bg-soft-blue { background-color: #f0f4f9; }
        .text-navy { color: #1e3a8a; }
        .bg-navy { background-color: #1e3a8a; }
        .btn-navy { background-color: #1e3a8a; color: white; border: none; }
        .btn-navy:hover { background-color: #172554; color: white; }
        
        .icon-box-navy { 
            background: #1e3a8a; color: white; 
            width: 38px; height: 38px; 
            display: flex; align-items: center; justify-content: center; 
            border-radius: 10px; 
        }

        .rounded-4 { border-radius: 12px !important; }
        
        .search-input-wrap { position: relative; }
        .search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #64748b; }
        .form-control-clean { 
            padding-left: 40px !important; 
            border: 1px solid #e2e8f0 !important; 
            background: #f8fafc !important;
            border-radius: 8px !important;
        }

        .deal-card { transition: transform 0.2s; }
        .deal-card:hover { transform: translateY(-2px); }
        
        .status-accent { width: 5px; min-height: 100%; }
        
        .deal-title { font-size: 1.05rem; }
        .deal-description { 
            font-size: 0.88rem; 
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .ml-48 { margin-left: 48px; }

        /* Soft Badge Colors */
        .badge-soft-warning { background-color: #fef3c7; color: #d97706; }
        .badge-soft-success { background-color: #dcfce7; color: #16a34a; }
        .badge-soft-danger { background-color: #fee2e2; color: #dc2626; }
        .badge-soft-info { background-color: #e0f2fe; color: #0284c7; }
      `}</style>
    </div>
  );
};

export default AdminDealList;